import { Injectable } from '@angular/core';
import { ResponseFromFetch } from '../models/response-from-fetch.model';
import { RequestInfo } from '../models/request-info.model';
import { CypherService } from './cypher.service';
import { environment } from '../../../environments/environment';
import { BdbHeadersModel } from '../models/bdb-headers.model';
import { BdbEncryptService, BdbSessionStrgService } from '@npm-bbta/bbog-cm-auth-lib';
import { ChannelService } from './channel.service';
import { SessionStorageKeysEnum } from '../models/session-storage-keys-enum';
import { ProductsEnum } from '../enums/products-enum';

@Injectable()
export class BdbRequestService {
    private readonly CONTENT_TYPE = 'Content-Type';
    private readonly APPLICATION_JSON = 'application/json';
    private readonly productsEnum = ProductsEnum;
    constructor(
        private readonly bdbCypherService: CypherService,
        private readonly bdbEncryptservice: BdbEncryptService,
        private readonly bdbSessionStorageService: BdbSessionStrgService,
        private readonly channelService: ChannelService
    ) { }

    requestPostSandboxHeadersWithoutNewHeaders(url: string, secretKey: string, info?: RequestInfo): Promise<ResponseFromFetch> {
        const body = info.body;
        const bodyEncrypt = this.bdbCypherService.encryptHS256(body, secretKey);
        const infoHeaders = info.headers || {};
        const xStrcode = this.bdbCypherService.hashMd5(bodyEncrypt);
        const headers = this.addSandboxHeaders(infoHeaders, secretKey, xStrcode);
        return this.getFetchService(url, 'POST', headers, bodyEncrypt, true);
    }

    requestPostHeadersEncrypt(url: string, info: any): Promise<ResponseFromFetch> {
        const fullUrl = new URL(url);
        const infoHeaders = info.headers || {};
        const headers = this.addOldHeadersLoginEventEncrypted(infoHeaders);
        const body = info.body;
        return this.getFetchService(fullUrl, 'POST', headers, body);
    }

    public getStandardizedHeaders(identityType: string, identityNumber: string): BdbHeadersModel {
        const publicIp = this.bdbSessionStorageService.get(SessionStorageKeysEnum.PUBLIC_IP);
        const product = this.bdbSessionStorageService.get(SessionStorageKeysEnum.SITE);
        const uuidAuth = this.bdbSessionStorageService.get('uuidAuth');

        return {
            'X-Auth-Token': uuidAuth,
            'X-Channel': environment.platform,
            'X-GovIssueIdentType': identityType,
            'X-IdentSerialNum': identityNumber,
            'X-IPAddr': publicIp,
            'X-Product': product,
            'X-UuidAuth': uuidAuth
        };
    }

    private addSandboxHeaders(customHeaders: any, secretKey: string, xStrcode: string) {
        customHeaders['x-strcode'] = xStrcode;
        customHeaders['X-Auth-Token'] = secretKey;
        return customHeaders;
    }

    private getFetchService(url, method, headers?, body?, plainText?): Promise<ResponseFromFetch> {
        const bodyToSend = !plainText ? JSON.stringify(body) : body;
        return new Promise<ResponseFromFetch>((resolve, reject) => {
            fetch(url, {
                body: bodyToSend,
                headers,
                method
            }).then(async (res) => {
                const resAsText = await res.text();
                const response = new ResponseFromFetch();
                response.status = res.status;
                response.body = resAsText;
                console.log('Response from: ', url, response);
                resolve(response);
            }).catch((reason) => {
                reject(reason);
            });
        });
    }

    requestPostSandboxOldHeaders(url: string, secretKey: string, info?: RequestInfo): Promise<ResponseFromFetch> {
        console.log('antes de encriptar', info);
        console.log('secretKey', secretKey);
        const body = info.body;
        const bodyEncrypt = this.bdbCypherService.encryptHS256(body, secretKey);
        const infoHeaders = info.headers || {};
        let headers = this.addOldHeadersLoginEvent(infoHeaders);
        const xStrcode = this.bdbCypherService.hashMd5(bodyEncrypt);
        headers = this.addSandboxHeaders(headers, secretKey, xStrcode);
        return this.getFetchService(url, 'POST', headers, bodyEncrypt, true);
    }

    private addOldHeadersLoginEvent(headers: HeadersInit): HeadersInit {
        headers[this.CONTENT_TYPE] = this.APPLICATION_JSON;
        // tslint:disable-next-line: no-string-literal
        headers['channel'] = this.channelService.getChannel();
        // tslint:disable-next-line: no-string-literal
        headers['product'] = this.bdbSessionStorageService.get(SessionStorageKeysEnum.SITE);
        headers['aditional-Info'] = this.addAditionalInfoHeaders();
        const uuidAuth = this.bdbSessionStorageService.get('uuidAuth');
        if (uuidAuth && uuidAuth !== 'null') {
            headers['X-UuidAuth'] = uuidAuth;
        }
        return headers;
    }

    private addAditionalInfoHeaders(): string {
        const privateIp = this.bdbSessionStorageService.get(SessionStorageKeysEnum.PRIVATE_IP);
        return `private-ip=${privateIp};`;
    }

    private addOldHeadersLoginEventEncrypted(headers: HeadersInit): HeadersInit {
        headers[this.CONTENT_TYPE] = this.APPLICATION_JSON;
        // tslint:disable-next-line: no-string-literal
        headers['channel'] = this.bdbEncryptservice.encrypt(this.channelService.getChannel());
        // tslint:disable-next-line: no-string-literal
        headers['product'] = this.bdbEncryptservice.encrypt(this.bdbSessionStorageService.get(SessionStorageKeysEnum.SITE));
        headers['aditional-Info'] = this.bdbEncryptservice.encrypt(this.addAditionalInfoHeaders());
        const uuidAuth = this.bdbSessionStorageService.get('uuidAuth');
        if (uuidAuth && uuidAuth !== 'null') {
            headers['X-UuidAuth'] = this.bdbEncryptservice.encrypt(uuidAuth);
        }
        return headers;
    }
    public requestPostWithOldHeaders(url: string, info?: RequestInfo): Promise<ResponseFromFetch> {
        const fullUrl = new URL(url);
        const infoHeaders = info.headers || {};
        const headers = this.addOldHeadersLoginEvent(infoHeaders);
        const body = info.body;
        return this.getFetchService(fullUrl, 'POST', headers, body);
    }

    requestPostSandbox(url: string, secretKey: string, info?: RequestInfo): Promise<ResponseFromFetch> {
        const body = info.body;
        const bodyEncrypt = this.bdbCypherService.encryptHS256(body, secretKey);
        const xStrcode = this.bdbCypherService.hashMd5(bodyEncrypt);
        const infoHeaders = info.headers || {};
        const headers = this.addSandboxHeaders(infoHeaders, secretKey, xStrcode);
        return this.getFetchService(url, 'POST', headers, bodyEncrypt, true);
    }

    getStandardizedHeadersForSandboxv3(identityType: string, identityNumber: string): BdbHeadersModel {
        const publicIp = this.bdbSessionStorageService.get(SessionStorageKeysEnum.PUBLIC_IP);
        const site = this.bdbSessionStorageService.get(SessionStorageKeysEnum.SITE);
        const uuid = this.bdbSessionStorageService.get(SessionStorageKeysEnum.UUID);
        const rqUuid = this.bdbSessionStorageService.get(SessionStorageKeysEnum.UUID_AUTH);
        const xname = this.productsEnum[site].apiName;
        return {
            'X-Channel': this.channelService.getChannel(),
            'X-CustIdentType': identityType,
            'X-CustIdentNum': identityNumber,
            'X-IPAddr': publicIp,
            'X-CompanyId': '001',
            'X-Name': xname,
            'X-RqUID': rqUuid ?? uuid,
            'X-User-Agent': navigator.userAgent
        };
    }

    public getDispatcherHeaders(): BdbHeadersModel {
        const headers: BdbHeadersModel = {};
        headers['X-Channel'] = environment.channel;
        headers['X-Product'] = '4';
        headers['X-CustIdentType'] = 'CC';
        headers['X-IdentSerialNum'] = '99999999';
        return headers;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/bdb-request.service.ts